import React from "react";
import SignUpForm from "./SignUpForm";
import { useSelector, useDispatch } from "react-redux";
import { selectors } from "@ReduxStore/reducers";
import { Creators } from "@ReduxStore/actionTypes";
import { Member } from "@Src/constants";
import { getAuthError, fireHeapTrackingEvent, errorLocations } from "../authHelpers";
import { useLingui } from "@lingui/react";
import { useLocation } from "react-router";

import "./SignUp.scss";

const {
  COMPLETING_SETUP_FAILURE,
  COMPLETING_SETUP_SUCCESSFUL,
  REGISTRATION_FAILURE,
  REGISTRATION_SUCCESSFUL,
  REGISTER_WITH_MEMBER_KEY,
  REGISTER_WITH_CODE,
} = Member;

const {
  getRegistrationStatus,
  getRegistrationType,
  getApprovedDomains,
  getProgramMemberKeyLabel,
  getProgramMemberKeyDescription,
  getCodeErrorMsg,
  getProgramWelcomeText,
  getRegistrationStatusText,
  getRegistrationError,
  getDirectedParams,
  getMember,
  getMemberCvtStatus,
} = selectors;

const { createMember, completeMemberProfile, completeAccountSetup, setSignUpStep } = Creators;

function SignUp(props) {
  const { _ } = useLingui();
  const dispatch = useDispatch();
  const { state: locationState } = useLocation();
  const helpNumber = useSelector(selectors.getHelpPhoneNumber);
  const [values, setValues] = React.useState({});
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [isReady, setIsReady] = React.useState(false);
  const [token, setToken] = React.useState(null);

  const { isCompleteProfile, handleRegistrationSuccess, screen, handleAuthSuccess } = props;

  const registrationType = useSelector(getRegistrationType);
  const approvedDomains = useSelector(getApprovedDomains);
  const memberKeyLabel = useSelector(getProgramMemberKeyLabel);
  const memberKeyDescription = useSelector(getProgramMemberKeyDescription);
  const cvtStatus = useSelector(getMemberCvtStatus);
  const codeErrorMsg = useSelector(getCodeErrorMsg);
  const programWelcomeText = useSelector(getProgramWelcomeText);
  const registrationStatus = useSelector(getRegistrationStatus);
  const registrationStatusText = useSelector(getRegistrationStatusText);
  const registrationError = useSelector(getRegistrationError);

  const registration = React.useMemo(
    () => ({
      status: registrationStatus,
      text: registrationStatusText,
      error: registrationError,
    }),
    [registrationStatus, registrationStatusText, registrationError],
  );

  const directedParams = useSelector(getDirectedParams);
  const member = useSelector(getMember);

  React.useEffect(() => {
    let obj = {
      memberID: "",
      firstName: "",
      lastName: "",
      email: "",
      registrationCode: "",
      email_domain: "",
      password: "",
      passwordStr: 0,
      confirmPassword: "",
      allowEmailEdit: true,
    };
    if (isCompleteProfile) {
      obj = {
        ...obj,
        memberID: member.member_key || "",
        firstName: member.first_name !== "Valued" ? member.first_name : "",
        lastName: member.last_name !== "Member" ? member.last_name : "",
        email: member.email || "",
        allowEmailEdit: member.email ? false : true,
      };
    } else if (directedParams) {
      obj = {
        ...obj,
        memberID: directedParams.member_key || "",
        firstName: directedParams.first_name || "",
        lastName: directedParams.last_name || "",
        email: directedParams.email || "",
        registrationCode: directedParams.registration_code || "",
        email_domain: directedParams.email_domain || "",
      };
    }

    setValues(obj);
    setIsReady(true);
  }, [isCompleteProfile, member, directedParams, setValues, setIsReady]);

  const onValueChange = (event) => {
    if (error) setError(null);
    setValues({
      ...values,
      [event.target.name]: event.target.value,
    });
  };

  const onPasswordChange = (pass, passStr = 0) => {
    setValues({
      ...values,
      password: pass,
      passwordStr: passStr,
    });
  };

  const onCaptchaChange = (token) => {
    setToken(token);
  };

  const watchRegistrationSuccess = React.useCallback(() => {
    if ([REGISTRATION_SUCCESSFUL, COMPLETING_SETUP_SUCCESSFUL].includes(registration.status)) {
      handleAuthSuccess();
      setLoading(false);
      fireHeapTrackingEvent("successful signup");
    }
  }, [registration.status, handleAuthSuccess]);

  const watchRegistrationError = React.useCallback(() => {
    if ([REGISTRATION_FAILURE, COMPLETING_SETUP_FAILURE].includes(registration.status)) {
      setLoading(false);
      const isMcid = registrationType === REGISTER_WITH_MEMBER_KEY;

      setError((prevError) =>
        getAuthError(registration.error, helpNumber, isMcid, isCompleteProfile),
      );
    }
  }, [registration.status, registration.error, registrationType, helpNumber, isCompleteProfile]);

  React.useEffect(() => {
    if (registration) {
      watchRegistrationSuccess();
      watchRegistrationError();
    }
  }, [registration, watchRegistrationError, watchRegistrationSuccess]); // Only depend on registration.status to avoid infinite loops

  const showGeneralError = () => {
    if (error && error.location === errorLocations.GENERAL) {
      return (
        <span
          className={`SignUp-generalError t-body-small ${screen} ${registrationType === REGISTER_WITH_CODE ? "code" : ""}`}
          dangerouslySetInnerHTML={{ __html: _(error.errorMsg) }}
        />
      );
    }
  };

  const completeSignUp = () => {
    const isAdminCompleteProfile = locationState && locationState.isAdminCompleteProfile;

    const shouldNotCreateMember =
      cvtStatus === Member.CVT_STATUS.NEEDS_PASSWORD || isAdminCompleteProfile;

    // should combine the local-part of the email and the domain when using restricted domains,
    // otherwise, just uses the entered full address because email_domain should be empty
    const emailAddress = `${values.email.trim()}${values.email_domain}`;

    setLoading(true);
    if (isCompleteProfile && shouldNotCreateMember) {
      const memberProfile = {
        first_name: values.firstName,
        last_name: values.lastName,
        email: emailAddress,
        password: values.password.trim(),
      };

      if (isAdminCompleteProfile) {
        dispatch(completeAccountSetup(memberProfile));
      } else {
        dispatch(completeMemberProfile(memberProfile));
      }
    } else {
      dispatch(
        createMember({
          registration_code: values.registrationCode.trim() || null,
          member_key: values.memberID.trim() || null,
          first_name: values.firstName,
          last_name: values.lastName,
          email: emailAddress,
          password: values.password.trim(),
          token: token,
        }),
      );
    }
  };

  if (!isReady) return null;
  return (
    <div className="SignUp">
      <SignUpForm
        values={values}
        onValueChange={onValueChange}
        onPasswordChange={onPasswordChange}
        onCaptchaChange={onCaptchaChange}
        memberKeyLabel={memberKeyLabel}
        memberKeyDescription={memberKeyDescription}
        codeErrorMsg={codeErrorMsg}
        programWelcomeText={programWelcomeText}
        handleRegistrationSuccess={handleRegistrationSuccess}
        registration={registration}
        registrationType={registrationType}
        approvedDomains={approvedDomains}
        completeSignUp={completeSignUp}
        error={error}
        loading={loading}
        screen={screen}
        isCompleteProfile={isCompleteProfile}
        setSignUpStep={setSignUpStep}
      />
      {showGeneralError()}
    </div>
  );
}

export default SignUp;
